import clsx from 'clsx';
import React, { FC, SelectHTMLAttributes, useMemo, useRef } from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[];
  placeholder?: string;
  inputSize?: 'x-small' | 'small' | 'medium' | 'large';
  showLabel?: boolean;
  label?: string;
  wrapperClassName?: string;
  selectClassName?: string;
  htmlFor?: string;
}

const Select: FC<SelectProps> = ({
  options,
  defaultValue,
  placeholder,
  onChange,
  inputSize = 'medium',
  wrapperClassName,
  selectClassName,
  label,
  showLabel = false,
  htmlFor,
  ...props
}) => {
  const uuidRef = useRef(`select-${Math.random().toString(36).slice(2, 11)}`);
  const uniqueId = htmlFor ?? uuidRef.current;
  const sizeClass = useMemo(() => {
    switch (inputSize) {
      case 'x-small':
        return 'min-h-6';
      case 'small':
        return 'min-h-8';
      case 'large':
        return 'min-h-12';
      case 'medium':
      default:
        return 'min-h-10';
    }
  }, [inputSize]);

  return (
    <div className={clsx('relative flex flex-col gap-2', wrapperClassName)}>
      {showLabel && (
        <label
          htmlFor={uniqueId}
          className={clsx('text-base font-bold text-gray-900')}
        >
          {label}
          {props.required && '*'}
        </label>
      )}
      <select
        className={clsx(
          'px-4 text-gray-900 rounded w-full bg-white border border-solid border-gray-300 flex gap-4 items-center justify-between min-w-[200px] focus:outline focus:outline-2 focus:outline-blue-500 focus:outline-offset-2 text-base disabled:bg-gray-200 disabled:border-gray-400',
          sizeClass,
          selectClassName
        )}
        id={uniqueId}
        aria-label={!showLabel ? label : undefined}
        onChange={onChange}
        {...props}
      >
        {placeholder && !props.value && (
          <option value="" hidden>
            {placeholder}
          </option>
        )}
        {options?.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default Select;
